import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEditor, Descendant, BaseElement, BaseText } from 'slate';
import {
  Editable,
  RenderElementProps,
  Slate,
  withReact,
  ReactEditor
} from 'slate-react';
import { withHistory } from 'slate-history';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import '@assets/css/transcript.css';
import { useRealtimeEditorContext } from '@utils/providers/realtime-editor-context.tsx';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import { RealtimeParagraphElement } from '@components/editors/realtime-editor/body/realtime-paragraph-element.tsx';

type RealtimeSegment = {
  id: number;
  text: string;
  offset_ms: number;
  words: [string, number, number, number][];
  is_final: boolean;
  speaker?: string;
};

type Props = {
  index: number;
  segment: RealtimeSegment;
};

type RealtimeSpeakerElement = BaseElement & {
  type: 'speaker';
  id: number;
  speaker?: string;
  offset_ms?: number;
  children: Descendant[];
};

type RealtimeParagraphElementType = BaseElement & {
  type: 'paragraph';
  id: number;
  children: Descendant[];
};

type RealtimeTextElement = BaseText & {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

export const RealtimeEditor = ({ index, segment }: Props) => {
  const [editor] = useState(() => withHistory(withReact(createEditor())));
  const { updateFinalSegment } = useRealtimeContext();
  const { addEditor, removeEditor } = useRealtimeEditorContext();

  useEffect(() => {
    addEditor(index.toString(), editor);

    return () => {
      removeEditor(index.toString());
    };
  }, [index, editor, addEditor, removeEditor]);

  const initialValue = useMemo(() => {
    const initialValues: Descendant[] = [];

    // Add speaker element
    initialValues.push({
      type: 'speaker',
      id: index,
      speaker: segment.speaker || 'Speaker',
      offset_ms: segment.offset_ms,
      children: [
        {
          text: segment.speaker || 'Speaker'
        }
      ]
    } as RealtimeSpeakerElement);

    // Add paragraph element
    initialValues.push({
      type: 'paragraph',
      id: index,
      children: [
        {
          text: segment.text || ''
        }
      ]
    } as RealtimeParagraphElementType);

    return initialValues;
  }, [segment, index]);

  const handleChange = useCallback(
    (value: Descendant[]) => {
      // Extract text from the Slate value
      const newText = value
        .filter(node => 'type' in node && node.type === 'paragraph')
        .map(node => {
          if ('children' in node) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return node.children.map((child: any) => child.text || '').join('');
          }
          return '';
        })
        .join('\n');

      // Update the segment in context
      if (newText !== segment.text) {
        updateFinalSegment(index, newText);
      }
    },
    [index, segment.text, updateFinalSegment]
  );

  const renderElement = useCallback(
    (props: RenderElementProps) => {
      return (
        <RealtimeParagraphElement
          {...props}
          editor={editor as ReactEditor}
          index={index}
        />
      );
    },
    [editor, index]
  );

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    const { attributes, leaf } = props;
    let { children } = props;
    const customLeaf = leaf as RealtimeTextElement;

    if (customLeaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (customLeaf.italic) {
      children = <em>{children}</em>;
    }

    if (customLeaf.underline) {
      children = <u>{children}</u>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  return (
    <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
      <Editable
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder="Start typing..."
        spellCheck
        autoFocus={false}
        style={{
          minHeight: '2rem',
          outline: 'none'
        }}
      />
    </Slate>
  );
};
